import React from "react";
import {
  ServerPaginatedTable,
  Loader,
  DebouncedSearch,
  usePermission,
} from "../../../shared";
import { useSegmentReferences } from "../hooks";
import { Stack, TextField, Typography } from "@mui/material";
import InputControl from "../../../shared/components/InputControl";

export const SegmentReferences = ({ segmentId, referenceTypes = [] }) => {
  const { state, onGotoPage, onSetPageSize, onSearch, onReferenceTypeChange } =
    useSegmentReferences(segmentId);
  const couponEditPermission = usePermission("condition-modify");
  const pushNotificationEditPermission = usePermission(
    "push-notification-modify",
  );
  const pointPromotionViewPermission = usePermission("points-promotion-view");

  const referenceTypeOptions = React.useMemo(
    () => referenceTypes ?? [],
    [referenceTypes],
  );

  const columns = [
    {
      Header: "Used in",
      accessor: "used_in",
      Cell: ({ row }) => row.original.used_in || "-",
    },
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row }) => {
        const isCouponRule = row.original.type === "coupon_rule";
        const isPointPromotion = row.original.type === "points_promotion";
        const isPushNotification = row.original.type === "push_notification";

        const hasNavigation =
          (couponEditPermission && isCouponRule) ||
          (pushNotificationEditPermission && isPushNotification) ||
          (pointPromotionViewPermission && isPointPromotion);
        return (
          <Typography
            variant="body2"
            sx={{
              textDecoration: hasNavigation ? "underline" : "none",
              fontWeight: 400,
              cursor: hasNavigation ? "pointer" : "default",
            }}
            onClick={
              hasNavigation
                ? () => {
                    const path = isCouponRule
                      ? `/coupon-rules/edit/${row.original.id}`
                      : isPushNotification
                        ? `/push-notification/edit/${row.original.id}`
                        : isPointPromotion
                          ? `/point-promotions/details/${row.original.id}`
                          : "#";
                    if (path !== "#") {
                      window.open(path, "_blank");
                    }
                  }
                : null
            }
          >
            {row.original.name || "-"}
          </Typography>
        );
      },
    },
  ];

  if (state.isLoading) {
    return <Loader />;
  }

  return (
    <ServerPaginatedTable
      columns={columns}
      data={state.references}
      isLoading={state.isLoading}
      pagination={state.pagination}
      sortByGlobal={[]}
      defaultSearch={false}
      searchBar={false}
      manualSortBy={false}
      gotoPage={onGotoPage}
      setPageSize={onSetPageSize}
      filterSection={
        <Stack direction="row" gap={2} alignItems="center" width="100%">
          <DebouncedSearch
            value={state.searchText}
            onChange={onSearch}
            fullWidth
          />
          <InputControl
            type="dropdown"
            options={referenceTypeOptions}
            value={state.referenceType}
            onChange={onReferenceTypeChange}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            renderInput={(params) => <TextField {...params} label="Used In" />}
            sx={{ width: 200 }}
          />
        </Stack>
      }
    />
  );
};
